import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Toaster, toast } from 'sonner';
import {
  Layers,
  ArrowLeft,
  Loader2,
  Search,
  Plus,
  X,
  Play,
  Download,
  MapPin,
  Phone,
  Globe,
  Mail,
  Star,
  Building2,
  CheckCircle2,
  Clock,
  Save,
  Linkedin,
  Copy,
  ExternalLink,
  FileText,
  Users,
  ChevronDown,
  ChevronUp,
  Network
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTED_CATEGORIES = [
  "Studi di architettura", "Commercialisti", "Studi legali",
  "Agenzie immobiliari", "Dentisti", "Elettricisti",
  "Idraulici", "Ristoranti", "Palestre",
  "Parrucchieri", "Geometri", "Ingegneri",
  "Consulenti del lavoro", "Assicurazioni", "Autofficine"
];

const SUGGESTED_CITIES = [
  "Bergamo", "Brescia", "Milano", "Dalmine", "Seriate",
  "Treviglio", "Romano di Lombardia", "Clusone", "Lovere",
  "Lecco", "Como", "Monza", "Cremona", "Mantova"
];

export default function BatchSearchPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [catInput, setCatInput] = useState('');
  const [cityInput, setCityInput] = useState('');
  const [sources, setSources] = useState(["paginegialle", "google_maps"]);
  const [maxPerQuery, setMaxPerQuery] = useState(10);
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const pollRef = useRef(null);

  const addCategory = (cat) => {
    const trimmed = cat.trim();
    if (trimmed && !categories.includes(trimmed)) {
      setCategories(prev => [...prev, trimmed]);
    }
    setCatInput('');
  };

  const addCity = (city) => {
    const trimmed = city.trim();
    if (trimmed && !cities.includes(trimmed)) {
      setCities(prev => [...prev, trimmed]);
    }
    setCityInput('');
  };

  const toggleSource = (src) => {
    setSources(prev =>
      prev.includes(src) ? prev.filter(s => s !== src) : [...prev, src]
    );
  };

  const totalQueries = categories.length * cities.length * sources.length;

  const startBatch = async () => {
    if (categories.length === 0 || cities.length === 0) {
      toast.error('Aggiungi almeno una categoria e una città');
      return;
    }
    if (sources.length === 0) {
      toast.error('Seleziona almeno una fonte dati');
      return;
    }

    setLoading(true);
    setJob(null);

    try {
      const res = await axios.post(`${API_URL}/batch/start`, {
        categories,
        cities,
        sources,
        max_results_per_query: maxPerQuery
      });
      setJobId(res.data.job_id);
      toast.success('Ricerca batch avviata!');
    } catch (err) {
      toast.error('Errore nell\'avvio della ricerca');
      setLoading(false);
    }
  };

  const pollStatus = useCallback(async () => {
    if (!jobId) return;
    try {
      const res = await axios.get(`${API_URL}/scraper/status/${jobId}`);
      setJob(res.data);
      if (res.data.status === 'completed' || res.data.status === 'failed') {
        setLoading(false);
        clearInterval(pollRef.current);
        if (res.data.status === 'completed') {
          toast.success(`Completato! ${res.data.results?.length || 0} aziende trovate`);
        } else {
          toast.error('Ricerca fallita: ' + (res.data.error || 'Errore sconosciuto'));
        }
      }
    } catch {
      // ignore polling errors
    }
  }, [jobId]);

  useEffect(() => {
    if (jobId) {
      pollRef.current = setInterval(pollStatus, 3000);
      pollStatus();
      return () => clearInterval(pollRef.current);
    }
  }, [jobId, pollStatus]);

  const exportCSV = async () => {
    if (!jobId) return;
    try {
      const res = await axios.get(`${API_URL}/scraper/results/${jobId}/csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `batch_results_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      toast.success('CSV scaricato!');
    } catch {
      toast.error('Errore export CSV');
    }
  };

  const exportEmailsCSV = async () => {
    if (!jobId) return;
    try {
      const res = await axios.get(`${API_URL}/scraper/results/${jobId}/emails-csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `emails_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      toast.success('Email esportate in CSV!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Nessuna email da esportare');
    }
  };

  const exportEmailsTXT = async () => {
    if (!jobId) return;
    try {
      const res = await axios.get(`${API_URL}/scraper/results/${jobId}/emails-txt`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `emails_${new Date().toISOString().slice(0,10)}.txt`;
      a.click();
      toast.success('Lista email scaricata!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Nessuna email da esportare');
    }
  };

  const saveAllAsLeads = async () => {
    if (!jobId) return;
    try {
      const res = await axios.post(`${API_URL}/scraper/save-to-leads/${jobId}`);
      toast.success(res.data.message);
    } catch {
      toast.error('Errore nel salvataggio');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiato!');
  };

  const statusLabel = {
    pending: 'In attesa...',
    running: 'Scraping in corso...',
    enriching_web: 'Arricchimento email...',
    enriching_hunter: 'Verifica Hunter.io...',
    completed: 'Completato',
    failed: 'Errore'
  };

  const results = job?.results || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" richColors />

      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-600 text-white flex items-center justify-center">
                <Layers className="w-5 h-5" />
              </div>
              <span className="font-bold text-xl text-slate-900 tracking-tight">Ricerca Batch</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

        {/* Configuration Card */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="w-5 h-5" />
                Configura Ricerca Batch
              </CardTitle>
              <CardDescription>
                Seleziona categorie e città per cercare aziende automaticamente su più fonti
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">

              {/* Categories */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">Categorie di aziende</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Aggiungi categoria..."
                    value={catInput}
                    onChange={(e) => setCatInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addCategory(catInput)}
                    data-testid="batch-category-input"
                  />
                  <Button onClick={() => addCategory(catInput)} disabled={!catInput.trim()} data-testid="add-category-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {categories.map((cat, i) => (
                    <Badge key={i} variant="secondary" className="py-1.5 px-3 text-sm">
                      {cat}
                      <button onClick={() => setCategories(prev => prev.filter((_, j) => j !== i))} className="ml-2">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {SUGGESTED_CATEGORIES.filter(c => !categories.includes(c)).slice(0, 8).map(cat => (
                    <button
                      key={cat}
                      onClick={() => addCategory(cat)}
                      className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 transition"
                    >
                      + {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Cities */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">Città</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Aggiungi città..."
                    value={cityInput}
                    onChange={(e) => setCityInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addCity(cityInput)}
                    data-testid="batch-city-input"
                  />
                  <Button onClick={() => addCity(cityInput)} disabled={!cityInput.trim()} data-testid="add-city-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {cities.map((city, i) => (
                    <Badge key={i} variant="secondary" className="py-1.5 px-3 text-sm">
                      {city}
                      <button onClick={() => setCities(prev => prev.filter((_, j) => j !== i))} className="ml-2">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {SUGGESTED_CITIES.filter(c => !cities.includes(c)).slice(0, 8).map(city => (
                    <button
                      key={city}
                      onClick={() => addCity(city)}
                      className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 transition"
                    >
                      + {city}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sources */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">Fonti dati</Label>
                <div className="flex gap-3">
                  <button
                    onClick={() => toggleSource('paginegialle')}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 transition ${
                      sources.includes('paginegialle')
                        ? 'border-amber-500 bg-amber-50 text-amber-800'
                        : 'border-slate-200 text-slate-500'
                    }`}
                    data-testid="source-paginegialle"
                  >
                    <FileText className="w-4 h-4" />
                    PagineGialle
                    {sources.includes('paginegialle') && <CheckCircle2 className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => toggleSource('google_maps')}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 transition ${
                      sources.includes('google_maps')
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                        : 'border-slate-200 text-slate-500'
                    }`}
                    data-testid="source-google-maps"
                  >
                    <MapPin className="w-4 h-4" />
                    Google Maps
                    {sources.includes('google_maps') && <CheckCircle2 className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Max results per query */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">Max risultati per ricerca</Label>
                <div className="flex gap-2">
                  {[5, 10, 15, 20].map(n => (
                    <button
                      key={n}
                      onClick={() => setMaxPerQuery(n)}
                      className={`px-4 py-2 rounded-lg border-2 transition font-medium ${
                        maxPerQuery === n
                          ? 'border-slate-800 bg-slate-800 text-white'
                          : 'border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              {/* Summary & Start */}
              <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                <div className="text-sm text-slate-600">
                  {totalQueries > 0 ? (
                    <span>
                      <strong>{totalQueries}</strong> ricerche totali
                      ({categories.length} categorie x {cities.length} città x {sources.length} fonti)
                      &mdash; ~{Math.ceil(totalQueries * 0.5)} min
                    </span>
                  ) : (
                    <span className="text-slate-400">Seleziona categorie e città per iniziare</span>
                  )}
                </div>
                <Button
                  onClick={startBatch}
                  disabled={loading || totalQueries === 0}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="start-batch-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> In corso...</>
                  ) : (
                    <><Play className="w-4 h-4 mr-2" /> Avvia Ricerca Batch</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Progress */}
        {job && job.status !== 'completed' && job.status !== 'failed' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Loader2 className="w-5 h-5 animate-spin text-amber-600" />
                  <span className="font-semibold text-amber-800">
                    {statusLabel[job.status] || job.status}
                  </span>
                </div>
                {job.current_task && (
                  <p className="text-sm text-amber-700 mb-3">{job.current_task}</p>
                )}
                <div className="flex items-center gap-4 text-sm text-amber-700">
                  <span>Ricerche: {job.queries_completed || 0}/{job.total_queries || totalQueries}</span>
                  <span>Aziende trovate: {job.progress || 0}</span>
                </div>
                <div className="mt-3 h-2 bg-amber-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-600 rounded-full transition-all duration-500"
                    style={{ width: `${job.total_queries ? (job.queries_completed || 0) / job.total_queries * 100 : 0}%` }}
                  />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Results */}
        {job?.status === 'completed' && results.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            {/* Actions bar */}
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">
                {results.length} aziende trovate
              </h2>
              <div className="flex flex-wrap gap-2">
                <Button onClick={exportCSV} variant="outline" data-testid="export-csv-btn">
                  <Download className="w-4 h-4 mr-2" /> Esporta CSV
                </Button>
                <Button onClick={exportEmailsCSV} variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="export-emails-csv-btn">
                  <Mail className="w-4 h-4 mr-2" /> Email CSV
                </Button>
                <Button onClick={exportEmailsTXT} variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="export-emails-txt-btn">
                  <FileText className="w-4 h-4 mr-2" /> Email TXT
                </Button>
                <Button onClick={saveAllAsLeads} data-testid="save-all-leads-btn">
                  <Save className="w-4 h-4 mr-2" /> Salva tutti come Lead
                </Button>
              </div>
            </div>

            {/* Results table */}
            <Card className="border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="batch-results-table">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Azienda</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Indirizzo</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Contatti</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Fonte</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Rete LinkedIn</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r, idx) => {
                      const hunterEmail = r.hunter_best_email || '';
                      const webEmail = r.enrichment?.emails_found?.[0] || r.email || '';
                      const bestEmail = hunterEmail || webEmail;
                      const network = r.enrichment?.linkedin_network || {};
                      const isExpanded = expandedRow === idx;

                      return (
                        <React.Fragment key={idx}>
                          <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50/50">
                            <td className="py-3 px-4">
                              <div className="font-medium text-slate-900">{r.name}</div>
                              {r.category && (
                                <span className="text-xs text-slate-500">{r.category}</span>
                              )}
                              {r.rating && (
                                <span className="text-xs text-amber-600 ml-2">
                                  <Star className="w-3 h-3 inline fill-amber-400 text-amber-400" /> {r.rating}
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-slate-600 max-w-[200px] truncate">
                              {r.address || '-'}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex flex-col gap-1">
                                {r.phone && (
                                  <span className="text-xs flex items-center gap-1 text-slate-600">
                                    <Phone className="w-3 h-3" /> {r.phone}
                                  </span>
                                )}
                                {bestEmail && (
                                  <span className="text-xs flex items-center gap-1 text-emerald-700">
                                    <Mail className="w-3 h-3" /> {bestEmail}
                                  </span>
                                )}
                                {r.website && (
                                  <a
                                    href={r.website.startsWith('http') ? r.website : `https://${r.website}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs flex items-center gap-1 text-blue-600 hover:underline"
                                  >
                                    <Globe className="w-3 h-3" /> Sito
                                  </a>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <Badge variant="outline" className="text-xs">
                                {r.batch_source === 'paginegialle' ? 'PG' : r.source === 'paginegialle' ? 'PG' : 'Maps'}
                              </Badge>
                            </td>
                            <td className="py-3 px-4">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setExpandedRow(isExpanded ? null : idx)}
                                className="text-blue-700 hover:text-blue-800 hover:bg-blue-50"
                                data-testid={`linkedin-network-btn-${idx}`}
                              >
                                <Linkedin className="w-3.5 h-3.5 mr-1.5" />
                                Rete
                                {isExpanded ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
                              </Button>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex gap-1">
                                {bestEmail && (
                                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(bestEmail)} title="Copia email">
                                    <Copy className="w-3 h-3" />
                                  </Button>
                                )}
                                {r.pg_detail_url && (
                                  <a href={r.pg_detail_url} target="_blank" rel="noopener noreferrer">
                                    <Button variant="ghost" size="sm" title="Pagina PG">
                                      <ExternalLink className="w-3 h-3" />
                                    </Button>
                                  </a>
                                )}
                              </div>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr key={`net-${idx}`} className="bg-blue-50/60">
                              <td colSpan={6} className="px-4 py-3">
                                <div className="flex flex-wrap gap-2">
                                  {network.company_search && (
                                    <a href={network.company_search} target="_blank" rel="noopener noreferrer">
                                      <Button variant="outline" size="sm" className="border-blue-300 text-blue-700 hover:bg-blue-100 text-xs h-8">
                                        <Building2 className="w-3 h-3 mr-1.5" /> Profilo Azienda
                                        <ExternalLink className="w-2.5 h-2.5 ml-1" />
                                      </Button>
                                    </a>
                                  )}
                                  {network.people_at_company && (
                                    <a href={network.people_at_company} target="_blank" rel="noopener noreferrer">
                                      <Button variant="outline" size="sm" className="border-blue-300 text-blue-700 hover:bg-blue-100 text-xs h-8">
                                        <Users className="w-3 h-3 mr-1.5" /> Dipendenti
                                        <ExternalLink className="w-2.5 h-2.5 ml-1" />
                                      </Button>
                                    </a>
                                  )}
                                  {network.ex_employees && (
                                    <a href={network.ex_employees} target="_blank" rel="noopener noreferrer">
                                      <Button variant="outline" size="sm" className="border-purple-300 text-purple-700 hover:bg-purple-100 text-xs h-8">
                                        <Users className="w-3 h-3 mr-1.5" /> Ex-Dipendenti (Rete)
                                        <ExternalLink className="w-2.5 h-2.5 ml-1" />
                                      </Button>
                                    </a>
                                  )}
                                  {network.similar_companies && (
                                    <a href={network.similar_companies} target="_blank" rel="noopener noreferrer">
                                      <Button variant="outline" size="sm" className="border-emerald-300 text-emerald-700 hover:bg-emerald-100 text-xs h-8">
                                        <Building2 className="w-3 h-3 mr-1.5" /> Aziende Simili
                                        <ExternalLink className="w-2.5 h-2.5 ml-1" />
                                      </Button>
                                    </a>
                                  )}
                                  {network.industry_peers && (
                                    <a href={network.industry_peers} target="_blank" rel="noopener noreferrer">
                                      <Button variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-100 text-xs h-8">
                                        <Layers className="w-3 h-3 mr-1.5" /> Settore Italia
                                        <ExternalLink className="w-2.5 h-2.5 ml-1" />
                                      </Button>
                                    </a>
                                  )}
                                  {!Object.keys(network).length && (
                                    <span className="text-xs text-slate-400 py-1">Link LinkedIn disponibili dopo l'arricchimento</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          </motion.div>
        )}

        {/* No results */}
        {job?.status === 'completed' && results.length === 0 && (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <Layers className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">Nessun risultato</h3>
              <p className="text-slate-500">Prova con categorie o città diverse</p>
            </CardContent>
          </Card>
        )}

        {/* Error */}
        {job?.status === 'failed' && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-6 text-center">
              <p className="text-red-700 font-medium">Errore: {job.error || 'Ricerca fallita'}</p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
